import React, { useEffect, useRef, useState } from "react"

import { Form, Formik } from "formik"
import { gql } from "urql"
import { pipe, subscribe } from "wonka"

import createBlob from "src/createBlob"
import { adminClient as client } from "src/urql-client"

import {
  AutocompleteField,
  FormFailures,
  SubmitButton,
  handleFailure,
} from "components/Forms/Formik/hookComponents"
import ModalWithProvidedBody from "components/ModalWithProvidedBody"

const ExportNoShowData = () => {
  const formState = {
    organizationIds: [],
  }
  const [csvUrl, setCsvUrl] = useState<string | null>(null)
  const csvDownloadLink = useRef<HTMLAnchorElement>()

  const handleExport = (values, actions, closeModal) => {
    const { unsubscribe } = pipe(
      client.subscription(exportSubscription, values),
      subscribe(result => {
        if (result.error) {
          handleFailure(actions, [{ message: result.error?.message }])
          actions.setSubmitting(false)
          return
        }

        const { status, failures, csv } = result.data.consecutiveNoShowsExport
        if (failures.length > 0) {
          handleFailure(actions, failures)

          actions.setSubmitting(false)
          unsubscribe()
          return
        }

        if (status === "done") {
          const blobUrl = createBlob(csv, "text/csv")
          setCsvUrl(blobUrl)
          actions.setSubmitting(false)
          closeModal()
          unsubscribe()
        } else if (status) {
          actions.setStatus(status)
        }
      })
    )
  }

  useEffect(() => {
    if (!csvUrl) return
    csvDownloadLink.current.click()
    setCsvUrl(null)
    setTimeout(() => URL.revokeObjectURL(csvUrl), 0)
  }, [csvUrl])

  return (
    <>
      <a
        ref={csvDownloadLink}
        href={csvUrl}
        download={`consecutive no shows.csv`}
        className="hidden"
      >
        csv
      </a>

      <ModalWithProvidedBody
        modalTitle="Consecutive No Show Export"
        buttonText="Export"
        buttonClassName="btn btn-info mb-2"
      >
        {({ closeModal }) => (
          <Formik
            initialValues={formState}
            onSubmit={(values, actions) =>
              handleExport(values, actions, closeModal)
            }
          >
            <Form>
              <div className="modal-body">
                <AutocompleteField
                  name="organizationIds"
                  label="Organizations"
                  api="/api/admins/organizations/autocomplete_name"
                  isMulti
                />
              </div>

              <div className="modal-footer flex-col">
                <FormFailures />
                <div className="flex w-full justify-between">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-danger"
                  >
                    Cancel
                  </button>
                  <SubmitButton text="Export" />
                </div>
              </div>
            </Form>
          </Formik>
        )}
      </ModalWithProvidedBody>
    </>
  )
}

const exportSubscription = gql`
  subscription NoShowsExport($organizationIds: [ID!]!) {
    consecutiveNoShowsExport(organizationIds: $organizationIds) {
      status
      csv
      failures {
        message
      }
    }
  }
`

export default ExportNoShowData
